import { Component, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { FileUploadModule } from 'primeng/fileupload';
import { MessageModule } from 'primeng/message';

// API Service
import { LearnMoreApiService } from '../../services/learn-more-api.service';
import type { LearnMoreResponseAdminDto } from '../../api/models';

@Component({
  selector: 'app-learn-more-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    FileUploadModule,
    MessageModule,
  ],
  templateUrl: './learn-more-dialog.component.html',
  styleUrls: ['./learn-more-dialog.component.css'],
})
export class LearnMoreDialogComponent implements OnInit, OnDestroy {
  // Injected services
  private readonly ref = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);
  private readonly translateService = inject(TranslateService);
  private readonly apiService = inject(LearnMoreApiService);

  isEdit: boolean = false;
  itemId?: string;
  previewImageUrl: string | null = null;
  selectedFile: File | null = null;
  submitting = signal(false);
  errorMessage = signal('');

  // Form controls
  titleControl = new FormControl('', [
    Validators.required,
    Validators.minLength(3),
    Validators.maxLength(200),
  ]);
  descriptionControl = new FormControl('', [
    Validators.required,
    Validators.minLength(10),
    Validators.maxLength(1000),
  ]);
  linkUrlControl = new FormControl('', [Validators.required, this.urlValidator]);
  coverImageControl = new FormControl('');

  learnMoreForm: FormGroup = new FormGroup({
    title: this.titleControl,
    description: this.descriptionControl,
    linkUrl: this.linkUrlControl,
    coverImage: this.coverImageControl,
  });

  ngOnInit(): void {
    // Check if we're editing an existing learn more item
    if (this.config.data?.learnMore) {
      this.isEdit = true;
      const item: LearnMoreResponseAdminDto = this.config.data.learnMore;
      this.itemId = item.id;

      this.titleControl.setValue(item.title);
      this.descriptionControl.setValue(item.description);
      this.linkUrlControl.setValue(item.linkUrl);

      if (item.coverImage) {
        this.previewImageUrl = this.apiService.getCoverImageUrl(item.coverImage);
        this.coverImageControl.setValue(item.coverImage);
      }
    }
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  async onSubmit(): Promise<void> {
    if (!this.learnMoreForm.valid || this.submitting()) {
      // Mark all fields as touched to show validation errors
      Object.keys(this.learnMoreForm.controls).forEach((key) => {
        this.learnMoreForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.submitting.set(true);
    this.errorMessage.set('');

    try {
      const data = {
        title: this.titleControl.value!,
        description: this.descriptionControl.value!,
        linkUrl: this.linkUrlControl.value!,
      };

      if (this.isEdit && this.itemId) {
        // Update existing item
        await this.apiService.updateLearnMore(this.itemId, data, this.selectedFile || undefined);
      } else {
        // Create new item
        await this.apiService.createLearnMore(data, this.selectedFile || undefined);
      }

      // Close dialog with success
      this.ref.close({ success: true, isEdit: this.isEdit });
    } catch (error: any) {
      console.error('Failed to save learn more resource:', error);
      this.errorMessage.set(
        error?.error?.message || this.translateService.instant('learnMore.errors.saveFailed')
      );
    } finally {
      this.submitting.set(false);
    }
  }

  onCancel(): void {
    this.ref.close();
  }

  onImageSelect(event: any): void {
    const file = event.files[0];
    if (file) {
      this.selectedFile = file;

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewImageUrl = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  removeImage(): void {
    this.previewImageUrl = null;
    this.selectedFile = null;
    this.coverImageControl.setValue('');
  }

  // URL validator
  urlValidator(control: any) {
    if (!control.value) return null;
    const urlPattern = /^https?:\/\/.+\..+/;
    return urlPattern.test(control.value) ? null : { invalidUrl: true };
  }

  // Error message methods
  getTitleErrorMessage(): string {
    if (this.titleControl.errors?.['required']) {
      return this.translateService.instant('learnMore.errors.titleRequired');
    }
    if (this.titleControl.errors?.['minlength']) {
      return this.translateService.instant('learnMore.errors.titleMinLength');
    }
    if (this.titleControl.errors?.['maxlength']) {
      const maxLength = this.titleControl.errors?.['maxlength'].requiredLength;
      return this.translateService.instant('learnMore.errors.titleMaxLength', {
        max: maxLength,
      });
    }
    return '';
  }

  getDescriptionErrorMessage(): string {
    if (this.descriptionControl.errors?.['required']) {
      return this.translateService.instant('learnMore.errors.descriptionRequired');
    }
    if (this.descriptionControl.errors?.['minlength']) {
      return this.translateService.instant('learnMore.errors.descriptionMinLength');
    }
    if (this.descriptionControl.errors?.['maxlength']) {
      const maxLength = this.descriptionControl.errors?.['maxlength'].requiredLength;
      return this.translateService.instant('learnMore.errors.descriptionMaxLength', {
        max: maxLength,
      });
    }
    return '';
  }

  getLinkUrlErrorMessage(): string {
    if (this.linkUrlControl.errors?.['required']) {
      return this.translateService.instant('learnMore.errors.linkUrlRequired');
    }
    if (this.linkUrlControl.errors?.['invalidUrl']) {
      return this.translateService.instant('learnMore.errors.linkUrlInvalid');
    }
    return '';
  }
}
